// Tables/cells · ChecklistProgress
//
// Generic checklist-progress cell. Renders a compact `<ProgressBar>` + a
// `done / total` numeric readout. Tone is auto-derived when unset:
//   - done === 0          → neutral (muted grey)
//   - 0 < done < total    → amber (work in progress)
//   - done === total      → green (complete)
// Pass an explicit `tone` to override (e.g. red for a failed-required checklist).
//
// Replaces every entity-specific "progress + count" cell — e.g.
// `TransactionChecklistProgressCell`.

import type { ReactNode } from "react";
import { ProgressBar, type ProgressBarTone } from "@ds/display/ProgressBar";
import { TextValue } from "@ds/display/TextValue";
import { Box } from "@ds/layout/Box";
import { Inline } from "@ds/layout/Inline";

// ─── Inputs (data props — the model) ─────────────────────────────────────

/** Tone vocabulary for the progress fill. */
export type ChecklistProgressTone = "neutral" | "green" | "amber" | "red";

export interface ChecklistProgressInput {
  /** Items done. Clamped to [0, total]. */
  done: number;
  /** Total items. When 0, the cell renders an empty 0/0 progress (muted). */
  total: number;
  /**
   * Force a specific tone. When omitted, the tone is auto-derived:
   *   done === 0          → neutral
   *   0 < done < total    → amber
   *   done === total      → green
   */
  tone?: ChecklistProgressTone;
  /** Optional leading label rendered before the count (e.g. "Checks"). */
  label?: ReactNode;
  /** `data-testid` on the root container. */
  testId?: string;
}

// ─── (no Outputs — display-only) ─────────────────────────────────────────

export type ChecklistProgressProps = ChecklistProgressInput;

// ─── Tone resolution ─────────────────────────────────────────────────────

function deriveTone(done: number, total: number): ChecklistProgressTone {
  if (total <= 0 || done <= 0) return "neutral";
  if (done >= total) return "green";
  return "amber";
}

function toProgressBarTone(tone: ChecklistProgressTone): ProgressBarTone {
  if (tone === "neutral") return "muted";
  return tone;
}

// ─── Implementation ──────────────────────────────────────────────────────

export function ChecklistProgress({
  done,
  total,
  tone,
  label,
  testId,
}: ChecklistProgressProps) {
  const safeTotal = Math.max(0, total);
  const safeDone = Math.max(0, Math.min(done, safeTotal));
  const percent = safeTotal === 0 ? 0 : (safeDone / safeTotal) * 100;
  const effectiveTone = tone ?? deriveTone(safeDone, safeTotal);

  return (
    <Inline gap="xs" align="center" testId={testId}>
      {label != null && (
        <TextValue size="xs" tone="muted" nowrap>
          {label}
        </TextValue>
      )}
      {/* Flex-grow wrapper so the ProgressBar fills available row width.
          Box.grow sets `flex: 1 1 0` + `minWidth: 0` via tokens — no raw style. */}
      <Box grow size="none" testId="checklist-progress-bar">
        <ProgressBar percent={percent} tone={toProgressBarTone(effectiveTone)} size="sm" />
      </Box>
      <TextValue
        value={`${safeDone}/${safeTotal}`}
        size="xs"
        tone="muted"
        nowrap
        tabular
        testId="checklist-progress-count"
      />
    </Inline>
  );
}
